import React, { useEffect, useRef, useState } from 'react';
import '../../styles/webinar.css';
import { Link } from 'react-router-dom';

// Diligentsia Founder Series — session 1 registration page. Ported from Irij's "Event Registration"
// handoff: Webinar-Desktop.html above 767px and Webinar-Mobile.html at or below it. The two
// artboards differ in far more than widths — type scale, section padding, the hero art's position
// in the flow, the speaker rows stacking, the form going single-column — so the styling lives in
// styles/webinar.css rather than inline, which a media query could not override.
//
// Three documents govern this page and they do not all say the same thing:
//   - the design decides layout and wording;
//   - Webinar-Developer-Brief-Page-And-Form.docx decides what the form sends (field names, radio
//     values, HubSpot wiring, confirmation state, sticky mobile CTA) and overrides the design;
//   - Webinar-HOUSE-FACTS.docx is the source of truth for names, titles and the palette, so the
//     colour tokens in the stylesheet are its values rather than the design's. Typography is the
//     exception: the design's two faces are kept, because matching it on Poppins alone moves every
//     line break.
const LOGO = require('../../images/logo.png');
const HERO = require('../../images/landing-pages/webinar-assets/hero-graphic.png');
const NEIL = require('../../images/landing-pages/webinar-assets/foster-neil-bio.png');
const PAUL = require('../../images/landing-pages/webinar-assets/paul-morris.jpg');
const OLLIE = require('../../images/landing-pages/webinar-assets/ollie-austen.jpg');
const MARK = require('../../images/landing-pages/webinar-assets/call-button-mark.png');

// HubSpot passive capture. The account is on EU data hosting, so the loader must stay pointed at
// js-eu1 — the generic snippet from HubSpot's docs will not work. Portal id from the integration
// brief. Reason: no HubSpot tracking exists anywhere else in this app, but the id is checked
// before injecting anyway, because two loaders on one page break form detection.
const HUBSPOT_PORTAL_ID = '147324908';
const HUBSPOT_SCRIPT_ID = 'hs-script-loader';

// The four radio values are a BLOCKER in the developer brief: HubSpot segments the whole email
// sequence on them, and a casing or wording mismatch silently routes fifteen emails to nobody.
const ICP_OPTIONS = [
    { value: 'founder', label: "I'm raising for my own company" },
    { value: 'advisor', label: 'I advise companies through raises' },
    { value: 'investor', label: 'I invest in companies' },
    { value: 'other', label: 'Something else' },
];

// Session copy. Kept here rather than fetched, so the page is one file. The title and the three
// session names are still unconfirmed with the partner, so expect to edit these before go-live.
const SESSION = {
    // Split so the hand-drawn ring can wrap "Mistakes", and so the desktop line breaks are the
    // handoff's own <br>s. The mobile artboard lets the title flow, so those <br>s hide there.
    titleBefore: 'Top Ten Common ',
    titleRinged: 'Mistakes',
    titleAfter: ['That Can Stall Your', 'Raise or Exit'],
    eyebrow: 'Webinar Registration - Session 1 of 4',
    tagline: 'Eliminate predictable show stoppers prior to deals',
    // The mobile artboard abbreviates the date; both render and CSS picks one.
    date: 'Friday 11 September',
    dateShort: 'Fri 11 Sep',
    time: '12:00–12:45 BST',
    session2Title: "Series A Term Sheets: What's Negotiable and What Isn't",
    session3Title: 'What Changes When Investors Join Your Board',
    // The real Zoom/Teams link, once Jignesh supplies it. Empty hides the "Join on the day" button
    // on the confirmation rather than shipping a dead link.
    joiningLink: '',
};

// Plain-text title, for the session list and the closing CTA band.
const SESSION_TITLE = `${SESSION.titleBefore}${SESSION.titleRinged} ${SESSION.titleAfter.join(' ')}`;

// Hosted calendar file, linked from the confirmation. It has to stay a real hosted file rather than
// anything bundled: HubSpot's confirmation email links to the same URL, and it only opens as a
// calendar entry when the server sends Content-Type: text/calendar.
const CALENDAR_URL = '/downloads/founder-series-session-1.ics';

// The speakers section is three identically-shaped rows, each under its own label. Neil's carries a
// bio and the credential chips where the other two carry a quote and a closing note.
const SPEAKER_ROWS = [
    {
        label: 'Introduction',
        img: PAUL,
        name: 'Paul Morris',
        role: 'Ex-Head of Growth Advisory, BDO',
        quote: '"Not being prepared and a lack of data integrity can lead to price chipping, but actually more often causes deals to fail. Anything you can do to reduce this is immensely valuable."',
        note: 'Paul brings an investor and growth-advisory perspective, having worked with growth businesses as an investor, banker, non-executive director and adviser.',
    },
    {
        label: 'Main Speaker',
        img: NEIL,
        name: 'Neil Foster',
        role: 'Global Co-Chair of Technology Sector · Brown Rudnick LLP',
        bio: 'Neil is a leading VC Lawyer, he co-chairs Brown Rudnick’s Global Technology group and is a lead partner in Emerging Growth Companies & Venture Capital, with 25 years advising on cross-border transactions. He has written a chapter on the Top Ten Common Mistakes featured in the book "Masters of Raising Venture Capital" edited by Andrew Romans, Silicon Valley.',
        chips: ['Chambers UK · Corporate/M&A since 2010', 'Legal 500 · Leading Partner, Venture Capital', 'Author · Masters of Raising Venture Capital, 2024'],
    },
    {
        label: 'Wrap up',
        img: OLLIE,
        name: 'Ollie Austen',
        role: 'Founder, Startup 2 Standup',
        quote: '"Neil’s advice on this topic will be invaluable to companies looking to close their investment rounds or sell their businesses quickly and with certainty."',
        note: 'Ollie brings the founder perspective and his experience supporting entrepreneurs through Startup to Standup - one of the UK’s leading founder communities.',
    },
];

// How long the splash sits over the page before fading out.
const LOADER_MS = 2000;

const PRACTICE_STEPS = ['Learn what to look for.', 'Sign in with Free Diligence Account.', "Use Diligentsia's free checklist to apply the learning."];

/**
 * Injects the HubSpot tracking loader once, immediately before </body>.
 * Removing it on unmount would leave HubSpot's own globals behind, so the script is left in place
 * and re-mounts are guarded by the element id instead.
 */
const useHubspotTracking = () => {
    useEffect(() => {
        if (document.getElementById(HUBSPOT_SCRIPT_ID)) return;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = HUBSPOT_SCRIPT_ID;
        script.async = true;
        script.defer = true;
        script.src = `//js-eu1.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
        document.body.appendChild(script);
    }, []);
};

/** Holds the splash over the page for LOADER_MS, then releases it. */
const useSplash = () => {
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), LOADER_MS);
        return () => clearTimeout(timer);
    }, []);
    return isLoading;
};

/** Shows the sticky mobile CTA once the hero has scrolled out of view. */
const useStickyCta = (heroRef) => {
    const [isStuck, setIsStuck] = useState(false);
    useEffect(() => {
        const hero = heroRef.current;
        if (!hero || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), { threshold: 0 });
        observer.observe(hero);
        return () => observer.disconnect();
    }, [heroRef]);
    return isStuck;
};

/** The hand-drawn ring around "Mistakes" — two overlapping, counter-rotated ellipses. */
const TitleRing = () => (
    <svg className="web-ring" viewBox="0 0 220 90" fill="none" aria-hidden="true">
        <ellipse cx="110" cy="45" rx="104" ry="38" stroke="#FF4F6D" strokeWidth="5" transform="rotate(-4 110 45)" />
        <ellipse cx="113" cy="43" rx="101" ry="35" stroke="#FF4F6D" strokeWidth="3.5" opacity="0.55" transform="rotate(3 113 43)" />
    </svg>
);

/** The date, long on desktop and abbreviated on the mobile artboard. */
const SessionDate = () => (
    <span>
        <span className="web-date-long">{SESSION.date}</span>
        <span className="web-date-short">{SESSION.dateShort}</span>
    </span>
);

const WebinarLanding = () => {
    const [isRegistered, setIsRegistered] = useState(false);
    const heroRef = useRef(null);
    const isStuck = useStickyCta(heroRef);
    const isLoading = useSplash();

    useHubspotTracking();

    // Reason: the developer brief marks this a BLOCKER — HubSpot's own listener is on the same
    // submit event, so preventDefault() alone is allowed and stopPropagation() is not. Without
    // preventDefault the page reloads; with stopPropagation the submission silently never lands.
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsRegistered(true);
    };

    const { time: sessionTime, session2Title, session3Title, joiningLink } = SESSION;
    const heroArtAlt = 'The Diligentsia investment-readiness checklist, with Brown Rudnick and Startup 2 Standup';

    return (
        <div className="dlg-webinar">
            {/* Reason: kept mounted rather than unmounted so it can fade rather than cut. */}
            <div className={`web-loader${isLoading ? '' : ' is-done'}`} role="status" aria-live="polite">
                <div className="web-loader-ring" aria-hidden="true"></div>
                <div className="web-loader-label">Webinar</div>
                <span className="web-sr-only">Loading</span>
            </div>
            <div>
                <header className="web-header">
                    <Link to="/"><img className="web-logo" src={LOGO} alt="Diligentsia" /></Link>
                    <div className="web-series">WEBINAR SERIES</div>
                </header>

                <section ref={heroRef} className="web-hero">
                    <div className="wrap web-hero-inner">
                        <div className="web-hero-grid">
                            <div className="web-hero-copy">
                                <div className="web-eyebrow">{SESSION.eyebrow}</div>
                                <h1 className="web-h1">
                                    {SESSION.titleBefore}
                                    <span style={{ position: 'relative', display: 'inline-block' }}>
                                        {SESSION.titleRinged}
                                        <TitleRing />
                                    </span>
                                    {SESSION.titleAfter.map((line) => (
                                        <React.Fragment key={line}><br className="web-h1-br" />{' '}{line}</React.Fragment>
                                    ))}
                                </h1>
                                <div className="web-tagline">{SESSION.tagline}</div>
                                {/* The mobile artboard puts the art here, between the tagline and the meta
                                    rule; the desktop one keeps it beside the copy. Both render and CSS
                                    shows whichever the breakpoint calls for. */}
                                <img className="web-hero-art-m" src={HERO} alt={heroArtAlt} />
                                <div className="web-hero-meta">
                                    <SessionDate />
                                    <span className="web-dot">·</span>
                                    <span>{sessionTime}</span>
                                    <span className="web-dot">·</span>
                                    <span>Online</span>
                                    <span className="web-dot web-date-long">·</span>
                                    <span className="web-free">Free</span>
                                </div>
                                <div className="web-cta-row">
                                    <a className="web-cta" href="#register">Reserve your place →</a>
                                    <span className="web-cta-note">Takes under a minute</span>
                                </div>
                            </div>
                            <img className="web-hero-art" src={HERO} alt={heroArtAlt} />
                        </div>
                    </div>
                </section>

                <section style={{ borderTop: '1px solid #e4e4e9', backgroundColor: '#F5F6FF', marginBottom: 0 }}>
                    <div className="wrap web-sec web-issues">
                        <div className="web-issues-copy">
                            <div className="web-issues-h">Small issues can become big problems in a transaction.</div>
                            <div className="web-issues-body">Share rights. Equity. IP. Pre-emption rights. Contracts. Veto rights.</div>
                            <div className="web-issues-rule">Spot them early. Deal with them before they become blockers.</div>
                        </div>
                        <div className="web-quote-card">
                            <img className="web-quote-avatar" src={MARK} alt="Mark Bernstein" />
                            <div className="web-quote-text">
                                "Neil is one of the UK's most active transaction lawyers. I've been doing deals for 30 years and still learn every time he speaks. So will you. He is a highly entertaining speaker and not to be missed."
                            </div>
                            <div className="web-quote-by">
                                <strong>Mark Bernstein</strong>
                                <span>Founder &amp; CEO, Diligentsia</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section style={{ background: '#fff', borderTop: '1px solid #e4e4e9' }}>
                    <div className="wrap web-sec">
                        <div className="web-h2 web-speakers-h">Speakers</div>
                        {SPEAKER_ROWS.map(({ label, img, name, role, quote, note, bio, chips }) => (
                            <React.Fragment key={name}>
                                <div className="web-group-label">{label}</div>
                                <div className="web-spk-row">
                                    <div className="web-spk-id">
                                        <img className="web-spk-avatar" src={img} alt={name} />
                                        <div>
                                            <div className="web-spk-name">{name}</div>
                                            <div className="web-spk-role">{role}</div>
                                        </div>
                                    </div>
                                    <div className="web-spk-body">
                                        {bio
                                            ? <div className="web-spk-bio">{bio}</div>
                                            : <div className="web-spk-quote">{quote}</div>}
                                        {chips ? (
                                            <div className="web-chips">
                                                {chips.map((chip, i) => (
                                                    <span key={chip} className={`web-chip${i === chips.length - 1 ? ' is-plain' : ''}`}>{chip}</span>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="web-spk-note">{note}</div>
                                        )}
                                    </div>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </section>

                <section id="register" className="web-register">
                    <div className="wrap web-sec web-register-grid">
                        <div>
                            <div className="web-h2">Reserve your place</div>
                            <div className="web-ticks">
                                <div className="web-tick">
                                    <i>✓</i>
                                    <span>If there's something you'd like Neil to cover, feel free to submit a question.</span>
                                </div>
                                <div className="web-tick">
                                    <i>✓</i>
                                    <span>Can't make it live? Register anyway and tick the box for the recording.</span>
                                </div>
                            </div>
                            <div className="web-reg-meta">
                                <SessionDate />
                                <span className="web-dot-light">·</span>
                                <span>{sessionTime}</span>
                                <span className="web-dot-light">·</span>
                                <span>Online</span>
                                <span className="web-dot-light">·</span>
                                <span>Free</span>
                            </div>
                        </div>

                        {isRegistered ? (
                            // Reason: HubSpot's confirmation email can take minutes to arrive, so the on-page
                            // state has to carry the joining link and the calendar link itself — otherwise
                            // people assume the form failed and submit again.
                            <div className="web-confirm" role="status" aria-live="polite">
                                <div className="web-h2">You're registered.</div>
                                <div style={{ color: '#c9c9d2' }}>{SESSION.date}, {sessionTime}.</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
                                    <a className="web-cta" href={CALENDAR_URL}>Add to your calendar</a>
                                    {joiningLink ? (
                                        <a className="web-cta" style={{ background: 'transparent', border: '1px solid #3a3a44' }} href={joiningLink} target="_blank" rel="noreferrer">
                                            Join on the day
                                        </a>
                                    ) : null}
                                </div>
                                <div className="web-form-note" style={{ textAlign: 'left' }}>
                                    A confirmation email is on its way with the same links.
                                </div>
                            </div>
                        ) : (
                            <form id="webinarRegisterForm" className="web-form" onSubmit={handleSubmit}>
                                <div className="web-field-row">
                                    <label className="web-label">
                                        First name
                                        <input type="text" name="firstname" required />
                                    </label>
                                    <label className="web-label">
                                        Last name
                                        <input type="text" name="lastname" required />
                                    </label>
                                </div>
                                <div className="web-field-row">
                                    <label className="web-label">
                                        Email
                                        <input type="email" name="email" required />
                                    </label>
                                    <label className="web-label">
                                        Company
                                        <input type="text" name="company" required />
                                    </label>
                                </div>
                                <div>
                                    <div className="web-label" style={{ marginBottom: '10px' }}>Which is closest to you?</div>
                                    <div className="web-radio-group">
                                        {ICP_OPTIONS.map(({ value, label }) => (
                                            <label key={value} className="web-radio" htmlFor={`icp-${value}`}>
                                                <input type="radio" id={`icp-${value}`} name="series_icp" value={value} required />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <label className="web-label">
                                    Anything you'd like Neil to cover? (optional)
                                    <textarea rows="3" name="series_question" style={{ resize: 'vertical' }} />
                                </label>
                                <label className="web-check">
                                    <input type="checkbox" name="series_recording_only" />
                                    I can't make it live — send me the recording
                                </label>
                                <button className="web-submit" type="submit">Reserve your place →</button>
                                <div className="web-form-note">
                                    Takes a minute. We'll send the joining link and the online checklist — nothing else.
                                </div>
                            </form>
                        )}
                    </div>
                </section>

                <section style={{ backgroundColor: '#F5F6FF', marginBottom: 0 }}>
                    <div className="wrap web-sec web-two-col web-practice" style={{ padding: '80px 48px' }}>
                        <div>
                            <div className="web-practice-h">Put the session into practice with Diligentsia</div>
                            <div className="web-practice-body">
                                The issues covered in the session are easier to address when you know what to look for. Diligentsia's free online checklist gives you a practical way to work through them against your own business.
                            </div>
                        </div>
                        <div>
                            {PRACTICE_STEPS.map((step, i) => (
                                <div key={step} className="web-step">
                                    <span className="web-step-num">{`0${i + 1}`}</span>
                                    <span className="web-step-text">{step}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section style={{ background: '#fff', borderTop: '1px solid #e4e4e9', marginBottom: 0 }}>
                    <div className="wrap web-sec" style={{ padding: '88px 48px' }}>
                        <div className="web-two-col web-sessions-head">
                            <div className="web-sessions-h">More sessions are coming.</div>
                            <div className="web-sessions-body">
                                Session 1 is the first in a series of practical conversations for founders navigating growth, a raise or exit, and what happens beyond the pitch.
                            </div>
                        </div>
                        <div className="web-sessions-list">
                            <div className="web-session-card">
                                <div className="web-session-when">01 · September</div>
                                <div className="web-session-title-row">
                                    <span className="web-session-title">{SESSION_TITLE}</span>
                                    <span className="web-here">You are here</span>
                                </div>
                            </div>
                            <div className="web-session-row">
                                <div>02 · October</div>
                                <span>{session2Title}</span>
                            </div>
                            <div className="web-session-row">
                                <div>03 · November</div>
                                <span>{session3Title}</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="web-band">
                    <div className="wrap web-band-inner">
                        <div style={{ maxWidth: '56ch' }}>
                            <div className="web-band-title">{SESSION_TITLE}</div>
                            <div className="web-band-meta">
                                <SessionDate /> · {sessionTime} · Online · Free
                            </div>
                        </div>
                        <a className="web-band-cta" href="#register">Reserve your place →</a>
                    </div>
                </section>

                <footer className="web-footer">
                    <div className="wrap web-footer-inner">
                        <img src={LOGO} alt="Diligentsia" style={{ height: '20px' }} />
                        <span>Hosted by Diligentsia, in collaboration with Brown Rudnick and Startup 2 Standup</span>
                    </div>
                </footer>

                {!isRegistered && (
                    <a href="#register" className={`web-sticky-cta${isStuck ? ' is-visible' : ''}`}>
                        Reserve your place →
                    </a>
                )}
            </div>
        </div>
    );
};

export default WebinarLanding;
